import { type EditorElement } from '@vedalib/editor/lib/elements'
import { useInViewport } from 'ahooks'
import { useCallback, useRef } from 'react'

import { PreviewMode } from 'services/Store/Project/enums'
import type { Block, IBlockMode } from 'services/Store/Project/types'

import { FEATURE_FLAGS } from '../config'
import { useEntityViewTracking } from './useEntityViewTracking'
import { useGetSetViewedEl } from './useGetSetViewedEl'

interface IUseElementViewed {
  element?: EditorElement | null
  block?: Block | null
  mode: IBlockMode
}

export const useElementViewed = <T extends HTMLElement = HTMLDivElement>({
  element,
  block,
  mode,
}: IUseElementViewed) => {
  const refElViewed = useRef<T | null>(null)
  const viewedBlock = mode.previewMode === PreviewMode.scorm && FEATURE_FLAGS.viewedElement
  const [inViewPort] = useInViewport(refElViewed, { threshold: 0.5 })
  const needViewed = Boolean(inViewPort !== undefined || viewedBlock || element?.id)
  const { setViewedElement } = useGetSetViewedEl({ element, block })
  const setViewed = useCallback(
    () => setViewedElement({ inViewPort, endTime: new Date().toISOString() }),
    [inViewPort, setViewedElement],
  )

  useEntityViewTracking({
    inViewPort,
    setViewed,
    setUnViewed: setViewed,
    needViewed,
    exitObserve: inViewPort,
  })

  return { refElViewed, inViewPort }
}
